import type { ConnectionState } from "@omony/ui";
import { useEffect, useRef, useState } from "react";
import { queryClient, refreshSession } from "./query.js";

/**
 * Ein SSE-Stream je Client (ARCHITEKTUR §6, §10.7). Ereignisse tragen nur IDs – die Daten lädt
 * TanStack Query über die autorisierten Endpunkte nach. Wiederverbindung mit Backoff 1–30 s plus
 * Zufallsanteil; `lastEventId` sorgt dafür, dass nichts verloren geht.
 */

export interface RealtimeEvent {
  id: number | null;
  type: string;
  refId: string | null;
  data: Record<string, unknown>;
}

const TYPES = [
  "ready",
  "resync",
  "case.changed",
  "case.alert",
  "board.changed",
  "urgent.queued",
  "vitals.requested",
  "vitals.recorded",
  "staff.called",
  "queue.warn",
  "video.ended",
  "triage.updated",
  "presence.changed",
  "session.revoked",
  "device.revoked",
  "app.updated",
  "signage.changed",
];

/** Standard-Reaktion: passende Queries invalidieren */
export function invalidateFor(e: RealtimeEvent) {
  const caseId = typeof e.data.caseId === "string" ? e.data.caseId : e.refId;
  switch (e.type) {
    case "resync":
      void queryClient.invalidateQueries();
      break;
    case "case.changed":
    case "vitals.requested":
    case "vitals.recorded":
    case "video.ended":
    case "queue.warn":
    case "case.alert":
      if (caseId) void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
      void queryClient.invalidateQueries({ queryKey: ["patient-view"] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
      break;
    case "triage.updated":
      if (caseId) {
        void queryClient.invalidateQueries({ queryKey: ["smed", caseId] });
        void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      }
      break;
    case "board.changed":
    case "urgent.queued":
    case "presence.changed":
      void queryClient.invalidateQueries({ queryKey: ["board"] });
      break;
    case "session.revoked":
      void refreshSession();
      break;
    case "device.revoked":
      queryClient.clear();
      location.reload();
      break;
    case "signage.changed":
      void queryClient.invalidateQueries({ queryKey: ["signage"] });
      break;
  }
}

export function backoffMs(attempt: number, random = Math.random()) {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt - 1));
  return Math.round(base * (0.7 + 0.6 * random));
}

/**
 * Hält den Stream offen, solange `enabled`. `onEvent` erhält jedes Ereignis zusätzlich
 * (für Hinweise wie „Vitalwerte angefordert“). Liefert den Verbindungszustand fürs Band.
 */
export function useRealtime(enabled: boolean, onEvent?: (e: RealtimeEvent) => void): ConnectionState {
  const [state, setState] = useState<ConnectionState>({ status: "online" });
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  });

  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let lastId: number | null = null;
    let attempt = 0;
    let timer: number | undefined;
    let countdown: number | undefined;
    let stopped = false;

    const connect = () => {
      const url = lastId === null ? "/api/v1/events" : `/api/v1/events?lastEventId=${lastId}`;
      es = new EventSource(url);
      es.onopen = () => {
        const reconnected = attempt > 0;
        attempt = 0;
        setState({ status: "online" });
        // nach einer Lücke alles Relevante neu laden (Nachspielen deckt Ereignisse ab, nicht verpasste Antworten)
        if (reconnected) void queryClient.invalidateQueries();
      };
      for (const t of TYPES)
        es.addEventListener(t, (ev) => {
          const me = ev as MessageEvent<string>;
          if (me.lastEventId) lastId = Number(me.lastEventId);
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(me.data) as Record<string, unknown>;
          } catch {
            /* leer */
          }
          const e: RealtimeEvent = {
            id: me.lastEventId ? Number(me.lastEventId) : null,
            type: t,
            refId: typeof data.refId === "string" ? data.refId : null,
            data,
          };
          invalidateFor(e);
          handler.current?.(e);
        });
      es.onerror = () => {
        es?.close();
        es = null;
        if (stopped) return;
        attempt++;
        const wait = backoffMs(attempt);
        const until = Date.now() + wait;
        const tick = () => {
          const s = Math.max(1, Math.ceil((until - Date.now()) / 1000));
          setState(navigator.onLine ? { status: "reconnecting", seconds: s } : { status: "offline" });
        };
        tick();
        window.clearInterval(countdown);
        countdown = window.setInterval(tick, 1000);
        timer = window.setTimeout(() => {
          window.clearInterval(countdown);
          connect();
        }, wait);
      };
    };
    connect();
    const online = () => {
      if (!es) {
        window.clearTimeout(timer);
        window.clearInterval(countdown);
        connect();
      }
    };
    // Ein offener Stream merkt den Netzverlust oft erst nach dem TCP-Timeout – daher sofort schließen und anzeigen
    const offline = () => {
      es?.close();
      es = null;
      window.clearTimeout(timer);
      window.clearInterval(countdown);
      attempt = Math.max(attempt, 1);
      setState({ status: "offline" });
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      stopped = true;
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.clearTimeout(timer);
      window.clearInterval(countdown);
      es?.close();
    };
  }, [enabled]);

  return enabled ? state : { status: "online" };
}
